import { Spacing } from '@persona-id/design-tokens';
import { FormEvent, useMemo, useState } from 'react';
import { EventOrValueHandler, Field } from 'redux-form';
import styled from 'styled-components';

import InputField from 'dashboard/components/FormControls/InputField';
import SelectField, { Option } from 'dashboard/components/FormControls/SelectField';
import LocationSearchInput from 'dashboard/components/LocationSearchInput';
import {
  ADDRESS_FORMATS_BY_COUNTRY_CODE,
  AddressField,
  AddressFormat,
  DEFAULT_ADDRESS_FORMAT,
  DEFAULT_LABELS,
} from 'lib/constants/addresses';
import { COUNTRIES, CountryCode } from 'lib/constants/locales';
import PiiAttributes from 'lib/interfaces/PiiAttributes';
import { isRequired, matchesRegexp, withMessage } from 'lib/utils/formValidation';

const REQUIRED_FIELDS: Record<AddressField, CountryCode[]> = {
  address1: [],
  address2: [],
  city: ['US'],
  postalCode: ['GB', 'US'],
  subdivision: ['US'],
};

const Row = styled('div')`
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: ${Spacing[2]};
  grid-template-columns: repeat(6, 1fr);
`;

const ShortCol = styled('div')`
  grid-column: span 2;
`;

const LongCol = styled('div')`
  grid-column: span 4;
`;

const FullCol = styled('div')`
  grid-column: span 6;
`;

interface AddressInputAutocompleteProps {
  boundChangeFormValue: (field: string, value: string) => void;
  inputCountryCode: CountryCode;
  fieldRequired: boolean;
  disabled?: boolean;
}

const AddressInputAutocomplete = ({
  boundChangeFormValue,
  inputCountryCode,
  fieldRequired,
  disabled,
}: AddressInputAutocompleteProps) => {
  const [countryCode, setCountryCode] = useState<CountryCode>(inputCountryCode);

  const addressFormat = ADDRESS_FORMATS_BY_COUNTRY_CODE[countryCode] || DEFAULT_ADDRESS_FORMAT;

  const streetValidation = useMemo(() => {
    if (fieldRequired) {
      return [withMessage('Street address is required', isRequired)];
    } else {
      return [];
    }
  }, [fieldRequired]);

  const cityValidation = useMemo(() => {
    const label = addressFormat.labels.city || DEFAULT_LABELS.city;
    if (fieldRequired && REQUIRED_FIELDS.city.includes(countryCode)) {
      return [withMessage(`${label} is required`, isRequired)];
    } else {
      return [];
    }
  }, [countryCode, addressFormat, fieldRequired]);

  const postalCodeValidation = useMemo(() => {
    const label = addressFormat.labels.postalCode || DEFAULT_LABELS.postalCode;
    const postalCodeValidation = [];
    if (fieldRequired && REQUIRED_FIELDS.postalCode.includes(countryCode)) {
      postalCodeValidation.push(withMessage(`${label} is required`, isRequired));
    }
    if (addressFormat.postalCodeRegex) {
      postalCodeValidation.push(
        withMessage(
          `The ${label} you entered is invalid`,
          matchesRegexp(addressFormat.postalCodeRegex),
        ),
      );
    }
    return postalCodeValidation;
  }, [countryCode, addressFormat, fieldRequired]);

  const renderCountryOrderedAddress = () => {
    const fieldComponents: Record<AddressField, JSX.Element> = {
      address1: (
        <Field
          autoComplete='address-line1'
          name='street1'
          disabled={disabled}
          placeholder={addressFormat.labels.address1 || DEFAULT_LABELS.address1}
          type='text'
          validate={streetValidation}
          boundChangeFormValue={boundChangeFormValue}
          // TODO: [PER-2429] Make LocationSearchInput not ThemedFormInput dependent
          component={
            addressFormat.autocompletionField === 'address1' ? LocationSearchInput : InputField
          }
          {...(addressFormat.autocompletionField === 'address1' ? { theme: {} } : {})}
        />
      ),
      address2: (
        <Field
          autoComplete='address-line2'
          name='street2'
          disabled={disabled}
          type='text'
          placeholder={addressFormat.labels.address2 || DEFAULT_LABELS.address2}
          // TODO: [PER-2429] Make LocationSearchInput not ThemedFormInput dependent
          component={
            addressFormat.autocompletionField === 'address2' ? LocationSearchInput : InputField
          }
          {...(addressFormat.autocompletionField === 'address2' ? { theme: {} } : {})}
        />
      ),
      city: (
        <Field
          autoComplete='address-level2'
          name='city'
          disabled={disabled}
          type='text'
          placeholder={addressFormat.labels.city || DEFAULT_LABELS.city}
          validate={cityValidation}
          component={InputField}
        />
      ),
      postalCode: (
        <Field
          autoComplete='postal-code'
          name='postalCode'
          disabled={disabled}
          placeholder={addressFormat.labels.postalCode || DEFAULT_LABELS.postalCode}
          type='text'
          validate={postalCodeValidation}
          boundChangeFormValue={boundChangeFormValue}
          // TODO: [PER-2429] Make LocationSearchInput not ThemedFormInput dependent
          component={
            addressFormat.autocompletionField === 'postalCode' ? LocationSearchInput : InputField
          }
          {...(addressFormat.autocompletionField === 'postalCode' ? { theme: {} } : {})}
        />
      ),
      subdivision: (
        <SubdivisionFormField
          addressFormat={addressFormat}
          countryCode={countryCode}
          fieldRequired={fieldRequired}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          disabled={disabled}
        />
      ),
    };

    return addressFormat.formatting.map((row, rowIndex) => (
      <div key={rowIndex}>
        <Row>
          {row.map((fieldName, index) => {
            const fieldComponent = fieldComponents[fieldName];
            if (row.length === 1) {
              return <FullCol key={`${countryCode}-${index}`}>{fieldComponent}</FullCol>;
            } else {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              if (fieldName === 'address2') {
                return <ShortCol key={`${countryCode}-${index}`}>{fieldComponent}</ShortCol>;
              } else {
                return <LongCol key={`${countryCode}-${index}`}>{fieldComponent}</LongCol>;
              }
            }
          })}
        </Row>
      </div>
    ));
  };

  return (
    <>
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      <CountrySelector disabled={disabled} setCountryCode={setCountryCode} />
      {renderCountryOrderedAddress()}
    </>
  );
};

export default AddressInputAutocomplete;

const CountrySelector = ({
  disabled,
  setCountryCode,
}: {
  disabled: boolean;
  setCountryCode: (countryCode: CountryCode) => void;
}) => {
  return (
    <Field
      autoComplete='address-country-code'
      name='countryCode'
      component={SelectField}
      disabled={disabled}
      id={PiiAttributes.CountryCode}
      type='text'
      placeholder='Country code'
      /**
       * redux-form allows an input's onChange to be eventOrValue
       * but the onChange on the Field is inflexible.
       * We typecast this because we know the SelectField to always return a value.
       */
      onChange={
        ((value: string) => {
          setCountryCode(value as CountryCode);
        }) as EventOrValueHandler<FormEvent<HTMLSelectElement>>
      }
    >
      {Object.keys(COUNTRIES)
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        .map((code: keyof typeof COUNTRIES) => [COUNTRIES[code], code])
        .sort((a, b) => a[0].toLowerCase().localeCompare(b[0].toLowerCase()))
        .map(([countryName, code]) => (
          <Option key={code} value={code}>
            {countryName}
          </Option>
        ))}
    </Field>
  );
};

const SubdivisionFormField = ({
  addressFormat,
  countryCode,
  fieldRequired,
  disabled,
}: {
  addressFormat: AddressFormat;
  countryCode: CountryCode;
  fieldRequired: boolean;
  disabled: boolean;
}) => {
  const label = addressFormat.labels.subdivision || DEFAULT_LABELS.subdivision;
  const validation = useMemo(() => {
    return fieldRequired && REQUIRED_FIELDS.subdivision.includes(countryCode)
      ? [withMessage(`${label} is required`, isRequired)]
      : [];
  }, [countryCode, label, fieldRequired]);

  if (addressFormat.subdivisions.length) {
    return (
      <Field
        autoComplete='address-level1'
        name='subdivision'
        component={SelectField}
        disabled={disabled}
        type='text'
        placeholder={label}
        validate={validation}
      >
        {addressFormat.subdivisions.map((subdivision) => (
          <Option key={subdivision.code} value={subdivision.untranslatedName}>
            {subdivision.code}
          </Option>
        ))}
      </Field>
    );
  } else {
    return (
      <Field
        autoComplete='address-level1'
        name='subdivision'
        component={InputField}
        disabled={disabled}
        type='text'
        placeholder={label}
        validate={validation}
      />
    );
  }
};
